/**
 * @fileoverview Purpose tags: the defaults and the limits (P1 slice 5,
 * 2026-09-24. docs/03-domain-model.md Tag / PlaceTag).
 *
 * **Tags are the organization's own, copied in — not shared system rows.**
 * Every organization starts with the eleven below and may rename, reorder,
 * delete or add to them (owners and admins only; OQ-12 changed 2026-09-24).
 * docs/03 once had `org_id IS NULL` system tags that every organization
 * pointed at; a rename would then have renamed it for everyone, so each
 * organization gets its own copy instead.
 *
 * **This list is written out in four places that cannot import it**: the
 * migration that gave existing organizations their tags (immutable once
 * merged), `seeds/dev.sql`, `seeds/preview-bootstrap.sql` and `pnpm org:add`.
 * Tests hold each of them to this list (`packages/db/test/default-tags.test.ts`,
 * `apps/api/test-node/add-org.test.js`).
 *
 * `key` records which default a tag started as. It survives a rename, is
 * `null` for tags an organization made itself, and is what lets "the lunch
 * tag" be found later (US-03's time-of-day default) whatever it is now called.
 */
export const DEFAULT_PURPOSE_TAGS = [
  { key: 'lunch', label: 'ランチ' },
  { key: 'dinner', label: 'ディナー' },
  { key: 'cafe_talk', label: '打合せ・カフェ' },
  { key: 'drinks', label: '飲み会' },
  { key: 'takeout', label: 'テイクアウト' },
  { key: 'gift', label: '差し入れ・手土産' },
  { key: 'sweets', label: 'スイーツ' },
  { key: 'business', label: '接待' },
  { key: 'solo', label: '一人でも' },
  { key: 'late', label: '深夜' },
  { key: 'other', label: 'その他' },
] as const;

/** Characters (code points, not UTF-16 units) in a tag's name. */
export const TAG_LABEL_MAX_LENGTH = 20;
/** Tags one organization may have. A chip row of more stops being a choice. */
export const TAGS_PER_ORG_MAX = 50;

export type TextCheck = { ok: true; value: string } | { ok: false; reason: string };

function length(text: string): number {
  return [...text].length;
}

// eslint-disable-next-line no-control-regex -- rejecting control characters is the point
const CONTROL = /[\u0000-\u001f\u007f]/;

/** A tag name as it is stored: trimmed, NFC, 1–20 characters, one line. */
export function checkTagLabel(raw: unknown): TextCheck {
  if (typeof raw !== 'string') return { ok: false, reason: 'タグの名前を入力してください' };
  const value = raw.normalize('NFC').trim();
  if (value.length === 0) return { ok: false, reason: 'タグの名前を入力してください' };
  if (length(value) > TAG_LABEL_MAX_LENGTH) {
    return { ok: false, reason: `タグの名前は${TAG_LABEL_MAX_LENGTH}文字までです` };
  }
  if (CONTROL.test(value)) return { ok: false, reason: 'タグの名前に使えない文字があります' };
  return { ok: true, value };
}
